import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Check, AlertCircle, Calendar, MapPin, Scale, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminPrecios() {
  const navigate = useNavigate();
  const [puntosVenta, setPuntosVenta] = useState([]);
  const [listasPrecios, setListasPrecios] = useState([]);
  const [selectedPunto, setSelectedPunto] = useState('');
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [selectedLista, setSelectedLista] = useState('');
  const [currentAsignacion, setCurrentAsignacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [showConfirmAll, setShowConfirmAll] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedPunto && fecha) {
      loadAsignacion();
    }
  }, [selectedPunto, fecha]);

  const loadData = async () => {
    try {
      const [puntosRes, listasRes] = await Promise.all([
        axios.get(`${API}/puntos-venta`),
        axios.get(`${API}/listas-precios`),
      ]);
      setPuntosVenta(puntosRes.data.sort((a, b) => a.name.localeCompare(b.name, 'es')));
      // Sort listas by peso_promedio
      const sortedListas = listasRes.data.sort((a, b) => a.peso_promedio - b.peso_promedio);
      setListasPrecios(sortedListas);
      
      if (puntosRes.data.length > 0) {
        setSelectedPunto(puntosRes.data[0].id);
      }
    } catch (error) {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadAsignacion = async () => {
    try {
      const { data } = await axios.get(`${API}/asignacion-precios/${selectedPunto}?fecha=${fecha}`);
      setCurrentAsignacion(data);
      if (data?.lista_precios_id) {
        setSelectedLista(data.lista_precios_id);
      } else {
        setSelectedLista('');
      }
    } catch (error) {
      setCurrentAsignacion(null);
      setSelectedLista('');
    }
  };

  const handleSaveAll = async () => {
    setShowConfirmAll(false);
    setError('');
    setSuccess('');
    setSavingAll(true);
    try {
      const { data } = await axios.post(`${API}/asignar-lista-precios-todos`, {
        fecha,
        lista_precios_id: selectedLista,
      });
      setSuccess(`Lista asignada a ${data.total} puntos de venta`);
      await loadAsignacion();
      setTimeout(() => setSuccess(''), 4000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al asignar lista');
    } finally {
      setSavingAll(false);
    }
  };

  const handleSave = async () => {
    if (!selectedLista) {
      setError('Selecciona una lista de precios');
      return;
    }
    
    setError('');
    setSuccess('');
    setSaving(true);
    
    try {
      await axios.post(`${API}/asignar-lista-precios`, {
        punto_venta_id: selectedPunto,
        fecha,
        lista_precios_id: selectedLista,
      });
      setSuccess('Lista de precios asignada correctamente');
      await loadAsignacion();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al asignar lista');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  const selectedListaInfo = listasPrecios.find(l => l.id === selectedLista);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="admin-precios-page">

      {/* Modal confirmación asignar todos */}
      {showConfirmAll && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-bold text-zinc-900 mb-2">¿Asignar a todos los PDV?</h2>
            <p className="text-sm text-zinc-500 mb-1">
              Se asignará la lista <span className="font-semibold text-zinc-800">{selectedListaInfo?.nombre}</span> a <span className="font-semibold">{puntosVenta.length}</span> puntos de venta para la fecha <span className="font-semibold">{fecha}</span>.
            </p>
            <p className="text-sm text-amber-600 mb-5">Esta acción sobreescribirá las asignaciones existentes para esa fecha.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmAll(false)}
                className="flex-1 h-11 border border-zinc-300 rounded-xl font-medium text-zinc-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveAll}
                className="flex-1 h-11 bg-zinc-900 text-white rounded-xl font-medium"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-zinc-900">Asignar Precios</h1>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-4 py-4 space-y-6">
        {/* Messages */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 text-green-700">
            <Check className="w-5 h-5" />
            {success}
          </div>
        )}

        {/* Link to manage listas */}
        <button
          onClick={() => navigate('/admin/listas-precios')}
          className="w-full p-4 bg-blue-50 border border-blue-200 rounded-xl text-left"
        >
          <p className="font-medium text-blue-700">Administrar Listas de Precios</p>
          <p className="text-sm text-blue-600">Crear, editar o eliminar listas de precios por peso</p>
        </button>

        {/* Punto de Venta Selection */}
        <div>
          <label className="block text-zinc-500 text-sm font-medium mb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Punto de Venta
          </label>
          <select
            value={selectedPunto}
            onChange={(e) => setSelectedPunto(e.target.value)}
            className="w-full h-12 px-4 bg-white border border-zinc-300 rounded-xl text-base"
            data-testid="select-punto"
          >
            {puntosVenta.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Date */}
        <div>
          <label className="block text-zinc-500 text-sm font-medium mb-2 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Fecha
          </label>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="w-full h-12 px-4 bg-white border border-zinc-300 rounded-xl text-base"
            data-testid="input-fecha"
          />
        </div>

        {/* Current Assignment */}
        {currentAsignacion && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-sm text-green-700 mb-1">Asignación actual:</p>
            <p className="font-bold text-green-800 flex items-center gap-2">
              <Scale className="w-4 h-4" />
              Pollo {currentAsignacion.peso_promedio} kg
            </p>
          </div>
        )}

        {/* Lista de Precios Selection */}
        <div>
          <label className="block text-zinc-500 text-sm font-medium mb-3">
            Selecciona Lista de Precios
          </label>
          
          {listasPrecios.length === 0 ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
              <p className="text-yellow-800 mb-2">No hay listas de precios creadas</p>
              <button
                onClick={() => navigate('/admin/listas-precios')}
                className="text-yellow-700 underline"
              >
                Crear una lista de precios
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {listasPrecios.map((lista) => (
                <button
                  key={lista.id}
                  onClick={() => setSelectedLista(lista.id)}
                  className={`w-full p-4 rounded-xl text-left transition-all ${
                    selectedLista === lista.id
                      ? 'bg-zinc-900 text-white'
                      : 'bg-zinc-50 border border-zinc-200'
                  }`}
                  data-testid={`lista-${lista.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-lg">{lista.nombre}</p>
                      <p className={`text-sm ${selectedLista === lista.id ? 'text-zinc-300' : 'text-zinc-500'}`}>
                        Peso: {lista.peso_promedio} kg
                      </p>
                    </div>
                    {selectedLista === lista.id && (
                      <Check className="w-6 h-6" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Preview selected lista */}
        {selectedListaInfo && (
          <div className="p-4 bg-zinc-50 rounded-xl">
            <p className="text-sm text-zinc-500 mb-2">Vista previa de precios:</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(selectedListaInfo.precios || {}).slice(0, 6).map(([clave, precio]) => (
                <div key={clave} className="flex justify-between">
                  <span className="text-zinc-600">Clave {clave}:</span>
                  <span className="font-bold">${precio}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-8 pt-4 border-t border-zinc-200 space-y-3">
        <button
          onClick={handleSave}
          disabled={saving || savingAll || !selectedLista}
          className="w-full h-14 bg-zinc-900 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="btn-save-asignacion"
        >
          {saving ? (
            <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-5 h-5" />
              Asignar a este PDV
            </>
          )}
        </button>

        <button
          onClick={() => setShowConfirmAll(true)}
          disabled={saving || savingAll || !selectedLista}
          className="w-full h-14 bg-blue-600 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="btn-save-todos"
        >
          {savingAll ? (
            <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Globe className="w-5 h-5" />
              Asignar a todos los PDV
            </>
          )}
        </button>
      </div>
    </div>
  );
}
